import React from 'react';

import { SuggestionKind } from 'harper.js';
import { AlineButton } from 'src/components';
import { getSuggestionLabel } from 'src/editor/spellcheck-helpers';
import type { SpellcheckLint, SpellcheckSuggestion } from 'src/editor/spellcheck-vars';
import { makeStyles } from 'tss-react/mui';

export const usePillStyles = makeStyles()(theme => ({
  noSuggestion: { color: theme.appColors.placeholderType, fontSize: 12 },
  pill: {
    '&&': {
      fontSize: 12,
      height: 24,
      minWidth: 0,
      padding: '0 10px',
    },
    borderRadius: 999,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

interface SuggestionPillsProps {
  canReplace: boolean;
  lint: SpellcheckLint;
  onAccept: (lint: SpellcheckLint, suggestion: SpellcheckSuggestion) => void;
}

export function SuggestionPills({ canReplace, lint, onAccept }: SuggestionPillsProps): JSX.Element {
  const { classes } = usePillStyles();

  if (lint.suggestions.length === 0)
    return <span className={classes.noSuggestion}>No suggestion</span>;

  return (
    <>
      {lint.suggestions.map(suggestion => (
        <AlineButton
          key={`${suggestion.kind}:${suggestion.text}`}
          buttonClassName={classes.pill}
          color="primary"
          disabled={!canReplace}
          size="small"
          title={getSuggestionTitle(suggestion)}
          variant="outlined"
          onClick={() => onAccept(lint, suggestion)}
        >
          {getSuggestionLabel(suggestion.text)}
        </AlineButton>
      ))}
    </>
  );
}

function getSuggestionTitle(suggestion: SpellcheckSuggestion): string {
  if (suggestion.text === '') return 'Delete this text';

  const label = getSuggestionLabel(suggestion.text).toLowerCase();
  const target = suggestion.text === ' ' ? 'a space' : label;
  return suggestion.kind === SuggestionKind.InsertAfter
    ? `Insert ${target}`
    : `Replace with ${target}`;
}
